import { useState, useEffect, useRef } from 'react';
import { db, auth } from './firebase';
import { 
  collection, addDoc, updateDoc, deleteDoc, 
  doc, query, orderBy, onSnapshot, serverTimestamp 
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { motion, AnimatePresence } from 'framer-motion';
import './Manifestation.css';

// Audio Assets
import createSound from './assets/create.mp3';
import editSound from './assets/edit.mp3';
import magic from './assets/magic.mp3';
import deleteSound from './assets/delete.mp3';

export default function ManifestationPage() {
    
  const [items, setItems] = useState([]);
  const [inputText, setInputText] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
 const [suggestions, setSuggestions] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);

  // Audio Refs
  const audioCreate = useRef(new Audio(createSound));
  const audioAi = useRef(new Audio(magic));
  const audioEdit = useRef(new Audio(editSound));
  const audioDelete = useRef(new Audio(deleteSound));
  
  const handleAiGenerate = async () => {
    const user = auth.currentUser;
  if (!user) return alert("Please log in first!");

  // 🔑 Force token to exist
  await user.getIdToken(true);
    audioAi.current.currentTime = 0;
  audioAi.current.play().catch(() => {});
    setIsGenerating(true);
    setSuggestions([]); // Clear previous result

    try {
      const functions = getFunctions();
      // 'generateAIVision' must match the name exported in index.js
      const genVision = httpsCallable(functions, 'generateAIVision');
      
      const result = await genVision({ userId: auth.currentUser.uid });
        if (Array.isArray(result.data)) {
      setSuggestions(result.data);
    } else {
      console.error("Unexpected AI response:", result.data);
      setSuggestions([]);
    }

      // result.data contains the string returned from Gemini
    } catch (err) {
       console.error(err);

  const isLimitError =
    err.code === 'resource-exhausted' ||
    err.details?.includes('LIMIT_REACHED') ||
    err.message?.includes('LIMIT_REACHED');

  if (isLimitError) {
    if (window.navigator.vibrate) {
      window.navigator.vibrate([100, 50, 100]);
    }
    setShowLimitModal(true);
  } else {
    console.error("Unexpected AI error:", err);}
    } finally {
      setIsGenerating(false);
    }
  };
  const LimitModal = ({ isOpen, onClose }) => (
  <AnimatePresence>
    {isOpen && (
      <div className="modal-overlay">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="limit-modal-content"
        >
          <div className="modal-icon">⏳</div>
          <h2>Stop abusing AI because you are cooked😪😪</h2>
          <p>
            Write your own manifestation messages lil bro😌 
          </p>
          <button onClick={onClose} className="modal-close-btn">
            UNDERSTOOD
          </button>
        </motion.div>
      </div>
    )}
  </AnimatePresence>
);

 const acceptAiSuggestion = async (text, index) => {
    playSfx('create');
    await addDoc(collection(db, 'users', auth.currentUser.uid, 'manifestations'), {
    content: text,
    lockedIn: false,
    createdAt: serverTimestamp()
  });
    setSuggestions(prev => prev.filter((_, i) => i !== index));
  };

  const playSfx = (type) => {
    const sfx = type === 'create' ? audioCreate : type === 'edit' ? audioEdit : audioDelete;
    sfx.current.currentTime = 0;
    sfx.current.play().catch(() => {});
  };

  useEffect(() => {
    const unsubAuth = auth.onAuthStateChanged((user) => {
      if (user) {
        const q = query(collection(db, 'users', user.uid, 'manifestations'), orderBy('createdAt', 'desc'));
        return onSnapshot(q, (snap) => {
          setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });
      }
    });
    return () => unsubAuth();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!inputText.trim()) return;
    playSfx('create');
    await addDoc(collection(db, 'users', auth.currentUser.uid, 'manifestations'), {
      content: inputText,
      lockedIn: false,
      createdAt: serverTimestamp()
    });
    setInputText('');
  };

  const handleDelete = async (id) => {
    playSfx('delete');
    await deleteDoc(doc(db, 'users', auth.currentUser.uid, 'manifestations', id));
  };

  const startEdit = (m) => {
    setEditingId(m.id);
    setEditText(m.content);
  };

  const saveEdit = async (id) => {
    playSfx('edit');
    await updateDoc(doc(db, 'users', auth.currentUser.uid, 'manifestations', id), {
      content: editText
    });
    setEditingId(null);
  };

  return (
    <div className="manifest-root">
      <div className="aura-bg" />
      
      <header className="future-header">
        <h1 className="vision-title">2026 <span>VISIONS</span></h1>
        <form onSubmit={handleAdd} className="vision-input-box">
          <input 
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="What will you become?"
          />
          <button type="submit">SPEAK</button>
           <button 
  onClick={handleAiGenerate} 
  className={`ai-btn ${isGenerating ? 'loading' : ''}`}
  disabled={isGenerating}
>
  {isGenerating ? "CHANNELING..." : "✦ AI MANIFEST"}
</button>
        </form>
      </header>

      <div className="vision-grid">
        <AnimatePresence mode="popLayout">
            {/* 4 AI BOXES APPEAR HERE FIRST */}
   {Array.isArray(suggestions) && suggestions.map((text, index) => (
      <motion.div 
        layout
        key={`ai-${index}`}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.5 }}
        className="vision-card suggestion-card"
      >
        <p className="vision-text">{text}</p>
        <div className="suggestion-footer">
          <button className="btn-accept" onClick={() => acceptAiSuggestion(text, index)}>ACCEPT</button>
          <button className="btn-reject" onClick={() => setSuggestions(prev => prev.filter((_, i) => i !== index))}>✕</button>
        </div>
      </motion.div>
    ))}

    {/* THEN YOUR REGULAR SAVED ITEMS */}
          {items.map((m) => (
            <motion.div 
              layout
              key={m.id}
              initial={{ opacity: 0, scale: 0.9, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className={`vision-card ${m.lockedIn ? 'locked' : 'floating'}`}
            >
              <div className="card-top">
                <span className="date">{m.createdAt?.toDate().toLocaleDateString() || 'Pending'}</span>
                {!m.lockedIn && (
                  <div className="card-controls">
                    <button onClick={() => startEdit(m)}>✎</button>
                    <button onClick={() => handleDelete(m.id)}>✕</button>
                  </div>
                )}
              </div>

              {editingId === m.id ? (
                <div className="edit-zone">
                  <textarea value={editText} onChange={(e) => setEditText(e.target.value)} autoFocus />
                  <button onClick={() => saveEdit(m.id)}>UPDATE</button>
                </div>
              ) : (
                <p className="vision-text">{m.content}</p>
              )}

              {m.lockedIn ? (
                <div className="badge-confirmed">ETERNALIZED</div>
              ) : (
                
                <button className="sticker-btn btn-lock" onClick={() => updateDoc(doc(db, 'users', auth.currentUser.uid, 'manifestations', m.id), { lockedIn: true })}>
                  LOCK IN
                </button>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
      <LimitModal 
  isOpen={showLimitModal} 
  onClose={() => setShowLimitModal(false)} 
/>
    </div>
  );
}